import { cn } from "@reactive-resume/utils";
import { useEffect, useState } from "react";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  BRAND LOADER — état de chargement premium aux couleurs Mon CV Pro
 * ─────────────────────────────────────────────────────────────────────────────
 *  - Logo animé `Mon-CV-PRO-anime.svg` au centre (son propre cycle 6,4 s)
 *  - Anneau conique vert → violet qui tourne autour (animate-spin natif)
 *  - Variante `fullScreen` pour les garde-fous d'auth / route loading
 *  - Variante `inline` compacte pour les chargements en place
 *  - Texte optionnel (brand voice, français), barré si non fourni
 * ─────────────────────────────────────────────────────────────────────────────
 */

type BrandLoaderProps = {
  /** Plein écran (centré, fond défocalisé). */
  fullScreen?: boolean;
  /** Taille du visuel du logo (px). Défaut : 104 fullScreen, 48 sinon. */
  size?: number;
  /** Message affiché sous le loader. */
  text?: string;
  className?: string;
};

export const BrandLoader = ({ fullScreen = false, size, text, className }: BrandLoaderProps) => {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const displaySize = size ?? (fullScreen ? 104 : 48);
  const ringSize = Math.round(displaySize * 1.35);
  const label = text ?? (fullScreen ? "Préparation de votre espace…" : "Chargement…");

  const spinner = (
    <span
      className="relative grid place-items-center"
      style={{ width: ringSize, height: ringSize }}
      role="status"
      aria-label={label}
    >
      {/* Anneau conique animé — rotate natif Tailwind */}
      <span
        aria-hidden
        className={cn("absolute inset-0 rounded-full", !reduced && "animate-spin")}
        style={{
          background:
            "conic-gradient(from 0deg, #75DDA0 0%, #472CB3 45%, #472CB3 60%, #75DDA0 100%)",
          WebkitMask:
            "radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 3px))",
          mask: "radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 3px))",
        }}
      />

      {/* Logo animé au centre (cycle interne du SVG) */}
      <img
        src="/logo/Mon-CV-PRO-anime.svg"
        alt=""
        aria-hidden
        className="pointer-events-none select-none"
        style={{ width: displaySize, height: "auto" }}
        draggable={false}
      />
    </span>
  );

  if (fullScreen) {
    return (
      <div
        className={cn(
          "fixed inset-0 z-[2147482990] grid place-items-center bg-canvas/70 backdrop-blur-sm",
          className,
        )}
      >
        <div className="flex flex-col items-center gap-4">
          {spinner}
          <p className="text-sm font-medium text-text-muted">{label}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col items-center gap-2 py-6", className)}>
      {spinner}
      <p className="text-xs font-medium text-text-muted">{label}</p>
    </div>
  );
};
